// src/invoices/form.js — формата за фактура (Фаза 3.2).
// Легаси мост: window.invoiceForm (викат я таблото и детайлът на контакт).

import { el, clear, $, field, input, row } from '../shared/dom.js';
import { dateInput, isoToBg, localTodayIso, dateInputsValid } from '../shared/dates.js';
import { money, dfmt } from '../shared/format.js';
import { toast } from '../shared/toast.js';
import { api } from '../shared/api.js';
import { openModal, closeModal, confirmDialog } from '../shared/modal.js';
import { auditBlock } from '../shared/audit.js';
import { viesButton } from '../shared/vies.js';
import { contactPicker } from '../shared/pickers.js';
import { clientCombobox } from '../shared/clientCombobox.js';
import { searchInput } from '../shared/ui.js';
import { state, cq, activeCompanyObj, companyBooks, vatRateSelect, contacts as contactsCache, invoicesCache, setInvoices, canEditPerm } from '../shared/state.js';
import { showDual, toBgn } from '../../services/euroDisplay.js';
import { computeTotals } from '../../services/totals.js';
import { stockTypes, invoiceMovesStock } from '../../services/stock.js';
import { stockQtyText } from '../shared/stockQty.js';
import { basesFor, suggestedBasis, findBasis, OTHER_LABEL, OTHER_MAX } from '../shared/vatBases.js';
import { INV_STATUS } from './index.js';
import { printInvoice } from './print.js';
import { mailDocOverlay } from './mail.js';
import { unitDatalist, paymentSelect, lastPriceFor, lastPriceText, listPriceFor, listPriceText, repriceAuto } from '../shared/docLines.js';
import kontoCfg from '../../services/kontoConfig.js';
import advances from '../../services/advances.js';

// (Б.5) Падежът на нова фактура: издаване + срок по фирма (по подразбиране
// 14 дни). Празният падеж чупеше просрочията — нямаше от какво да се смятат.
const DEFAULT_DUE_DAYS = 14;
function dueDaysOf(company) {
  const d = Number(company && company.invoice_due_days);
  return d > 0 ? d : DEFAULT_DUE_DAYS;
}
// Данъчният режим на документа (ПРОМТ-7) — ключовете са TAX_TREATMENTS в
// services/docItems.js и deal_types в config/konto.config.json.
const TAX_TREATMENT_OPTS = [
  ['domestic', 'Вътрешна доставка (20 %)'],
  ['eu_b2b', 'ВОД към фирма в ЕС'],
  ['eu_b2c_oss', 'Продажба на физ. лице в ЕС (OSS)'],
  ['eu_b2c_domestic', 'Физ. лице в ЕС — българско ДДС'],
  ['reverse_charge', 'Обратно начисляване (чл. 163а)'],
  ['exempt', 'Освободена доставка'],
];
// Основанието за неначисляване се избира от меню (ПРОМТ-14) — списъкът,
// кой ред при кой режим се вижда и какво се предлага са в shared/vatBases.js.
// „Друго" е отделна опция: тогава стойността е написаното в полето под менюто.
const BASIS_OTHER = '__other__';

function plusDays(iso, days) {
  if (!iso) return '';
  const p = String(iso).slice(0, 10).split('-').map(Number);
  const d = new Date(p[0], p[1] - 1, p[2]);
  if (isNaN(d)) return '';
  d.setDate(d.getDate() + days);
  const p2 = (x) => (x < 10 ? '0' : '') + x;
  return d.getFullYear() + '-' + p2(d.getMonth() + 1) + '-' + p2(d.getDate());
}

// Копие на ред за нова фактура: артикулът и мярката се пренасят само ако
// ги има — стар ред без тях си остава свободен текст.
function copyItem(i2) {
  const r = { description: i2.description, quantity: i2.quantity, unit_price: i2.unit_price };
  if (i2.article_id != null && i2.article_id !== '') r.article_id = i2.article_id;
  if (i2.unit) r.unit = i2.unit;
  // Видът приход за Конто се пренася; приспаднатият аванс — НЕ: копието би
  // приспаднало същия аванс втори път.
  if (i2.revenue) r.revenue = i2.revenue;
  return r;
}

// opts.kind = 'proforma' (Макс, 20.10.2026: „нека проформата изглежда и
// работи както фактурата"): СЪЩАТА форма, без това, което проформата няма —
// фактурник, тип документ (известия), аванси, повтаряне, съставител/получил,
// заключване от Конто. Всяко подобрение на фактурата така важи и за нея.
// opts.statuses — статусите на проформата; opts.extraFoot(existing) — бутон
// „Превърни във фактура" (src/proformas/index.js).
export function invoiceForm(existing, contacts, opts) {
  if (!contacts) contacts = contactsCache();
  const inv = existing || {};
  const o = opts || {};
  const PF = o.kind === 'proforma';
  const BASE = PF ? '/proformas' : '/invoices';
  const NOUN = PF ? 'проформа' : 'фактура';
  const STATUSES = PF ? (o.statuses || {}) : INV_STATUS;
  // Заключена е САМО фактура, минала през експорт към Конто 6 — за всички
  // роли, включително админ (той първо отключва). Изпратена ≠ заключена:
  // всички роли редактират до заключването (решение на Макс, авг. 2026).
  // Проформата се заключва, когато е превърната във фактура (locked_at).
  const locked = !!(existing && existing.id && (PF ? existing.locked_at : existing.konto_exported_at));
  const isAdmin = !!(state().user && state().user.role === 'admin');
  // „Само преглед" (ОТЧЕТ-ПРОМТ-38, Д3): проформите са под правото „Фактури".
  // Без право за промяна бутоните, които пълнят формата или правят нов
  // документ, ги няма — прозорецът и без това не може да се запише.
  const canEdit = canEditPerm('invoices');
  const _company = activeCompanyObj() || {};
  // Етап 3: видът приход за Конто — по ред, само за фирма на „Импорт на
  // записвания" с повече от един вид (Маятико: сосове/дограма; Къщата ви:
  // услуга/средства на клиента). Иначе редовете отиват към първия вид и
  // менюто не се показва — нищо излишно на екрана. Вижда го и клиентът
  // (Макс, 03.11.2026): той знае дали редът е услуга или средства на
  // клиента, кантората не може да го познае по описанието. За клиента
  // менюто показва само името на вида, без номера на сметката.
  const _rec = kontoCfg.isRecords(_company) ? kontoCfg.recordsOf(kontoCfg.forCompany(_company)) : null;
  const _revs = _rec && _rec.revenues.length > 1 ? _rec.revenues : null;
  const _isClient = !!(state().user && state().user.role === 'client');
  // Отметката „Авансова фактура" има смисъл само при записванията (Кт 412) —
  // другаде не променя нищо и само би объркала. Заварен аванс я показва винаги.
  // За разлика от „Приход за Конто" я вижда и клиентът: дали плащането е
  // аванс, знае този, който издава фактурата.
  const _advOn = !PF && !!(_rec || inv.advance);
  const _books = PF ? [] : companyBooks(_company);
  let bookSel = null;
  if (_books.length > 1) {
    const cur = inv.book_start ? String(inv.book_start) : String(_books[0].start);
    bookSel = el('select', {}, ..._books.map((b) => el('option', { value: b.start, text: b.name, selected: String(b.start) === cur })));
    if (existing && existing.id) bookSel.disabled = true;
  }
  let items = (inv.items && inv.items.length ? inv.items.map((i) => ({ ...i })) : [{ description: '', quantity: 1, unit_price: 0 }]);

  const f = {
    contact_id: el('select', {}, el('option', { value: '', text: '— изберете клиент —' }), ...contacts.map((c) => el('option', { value: c.id, text: c.name, selected: inv.contact_id === c.id }))),
    issue_date: dateInput(inv.issue_date || localTodayIso()),
    // Проформата е „валидна до" — без срок по подразбиране (както досега).
    due_date: dateInput(inv.due_date || (existing && existing.id || PF ? '' : plusDays(inv.issue_date || localTodayIso(), dueDaysOf(activeCompanyObj())))),
    // „Само евро" (01.09.2026): менюто за валута падна. Скритото поле пази
    // валутата на ЗАВАРЕН документ (BGN/USD), за да не я подмени записът.
    currency: el('input', { type: 'hidden', value: inv.currency || 'EUR' }),
    vat_rate: vatRateSelect(inv.vat_rate),
    // Падащо меню (Промени 16.10, т. 3д) — свободният текст „в брой" отиваше
    // в Конто като банково плащане. Заварен текст се пази като опция.
    payment_method: paymentSelect(inv.payment_method),
    notes: el('textarea', { placeholder: 'Основание / бележки…' }, inv.notes || ''),
    recurring: el('input', { type: 'checkbox', checked: !!inv.recurring }),
    recurring_interval: el('select', {}, ...[['monthly', 'Всеки месец'], ['weekly', 'Всяка седмица'], ['yearly', 'Всяка година']].map(([v, l]) => el('option', { value: v, text: l, selected: (inv.recurring_interval || 'monthly') === v }))),
    recurring_next: dateInput(inv.recurring_next),
    tax_treatment: el('select', {}, ...TAX_TREATMENT_OPTS.map(([v, l]) => el('option', { value: v, text: l, selected: (inv.tax_treatment || 'domestic') === v }))),
    vat_basis: el('select', {}),
    vat_basis_other: input({ value: '', maxlength: String(OTHER_MAX), placeholder: 'напр. чл. 22 ЗДДС' }),
  };

  const _composerCur = (inv.created_by_name) || ((state().user && state().user.name) || '');
  const _molName = (_company && _company.mol) ? String(_company.mol).trim() : '';
  const composerSel = el('select', {}, el('option', { value: _composerCur, text: _composerCur || '—', selected: true }));
  api('/auth/users-list?company_id=' + (state().activeCompany || '')).then((list) => {
    const names = (list || []).map((u) => u.name).filter(Boolean);
if (_molName && names.indexOf(_molName) === -1) names.push(_molName);
        if (_composerCur && names.indexOf(_composerCur) === -1) names.unshift(_composerCur);
    if (names.length) { clear(composerSel); names.forEach((nm) => composerSel.appendChild(el('option', { value: nm, text: nm, selected: nm === _composerCur }))); }
  }).catch(() => {});

  const _docType0 = inv.doc_type || 'invoice';
  const docTypeSel = el('select', {}, ...[['invoice', 'Фактура'], ['debit', 'Дебитно известие'], ['credit', 'Кредитно известие']].map(([v, l]) => el('option', { value: v, text: l, selected: _docType0 === v })));
  const refSel = el('select', {}, el('option', { value: '', text: '— изберете фактура —' }), ...(invoicesCache() || []).filter((x) => (x.doc_type || 'invoice') === 'invoice').map((x) => el('option', { value: x.id, text: '№ ' + x.number + ' (' + dfmt(x.issue_date) + ')', selected: String(inv.ref_invoice_id) === String(x.id) })));
  const refField = field('Към фактура', refSel);
  // Дебитното известие изписва стока само с изрична отметка (Макс,
  // 16.10.2026): обикновено ДИ е за цена; с допълнително количество — „да".
  // Вижда се само при ДИ и само на фирма, чиито фактури изписват от склада.
  const debitMoves = el('input', { type: 'checkbox', checked: !!inv.debit_moves_stock });
  const _coStock = activeCompanyObj();
  const debitField = el('div', { class: 'field' }, el('label', { class: 'choice' }, debitMoves, el('span', { text: 'С допълнително количество — изписва стоката от склада' })));
  // Авансова фактура (Етап 3): плащане преди доставката. При записванията
  // за Конто редовете ѝ отиват по Кт 412; в окончателната се приспада с
  // „Приспадни аванс" (services/advances.js). Само при обикновена фактура.
  const advanceCb = el('input', { type: 'checkbox', checked: !!inv.advance });
  const advanceField = el('div', { class: 'field' }, el('label', { class: 'choice' }, advanceCb, el('span', { text: 'Авансова фактура (плащане преди доставката)' })));
  // Бутонът „Приспадни аванс" се прави по-долу (до „Копирай…") — дотогава
  // синхронизацията му е празна, иначе първото syncDocType() би го пипнало
  // преди да съществува.
  let _syncAdv = () => {};
  const syncDocType = () => {
    refField.style.display = (docTypeSel.value === 'invoice') ? 'none' : '';
    debitField.style.display = (docTypeSel.value === 'debit' && stockTypes(_coStock && _coStock.warehouse_mode).length && invoiceMovesStock(_coStock)) ? '' : 'none';
    advanceField.style.display = (_advOn && docTypeSel.value === 'invoice') ? '' : 'none';
    _syncAdv();
  };
  docTypeSel.addEventListener('change', syncDocType);
  docTypeSel.addEventListener('change', () => _hints.forEach((fn) => fn()));
  debitMoves.addEventListener('change', () => _hints.forEach((fn) => fn()));
  syncDocType();

  const itemsBody = el('tbody');
  const totalsBox = el('div', { class: 'totals' });
  function recompute() {
    // СЪЩАТА формула като сървъра (services/totals.js, преглед 17.09.2026):
    // екранът смяташе ДДС върху НЕзакръглената основа и при дробни
    // количества показваше „Общо" със стотинка разлика от записаното и
    // отпечатаното (0,5 × 166,75 → 100,05 на екрана, 100,06 в PDF-а).
    const t = computeTotals(items, Number(f.vat_rate.value) || 0);
    const sub = t.subtotal;
    const vr = Number(f.vat_rate.value) || 0;
    const vat = t.vat_amount;
    const _cur = f.currency.value;
    // Двойното „€ / лв." остава само на документи, издадени преди 01.09.2026
    // — екранът показва каквото ще излезе на печат. money() и за левовете:
    // toFixed даваше 0,00 € / 0.00 лв — два десетични знака в ЕДИН ред.
    const _iso = (f.issue_date.dataset && f.issue_date.dataset.iso) || '';
    const lv = (n) => showDual(_iso, _cur) ? ' / ' + money(toBgn(n), 'BGN') : '';
    clear(totalsBox);
    totalsBox.appendChild(el('div', { class: 'line' }, el('span', { text: 'Данъчна основа' }), el('span', { text: money(sub, f.currency.value) + lv(sub) })));
    totalsBox.appendChild(el('div', { class: 'line' }, el('span', { text: 'ДДС ' + vr + '%' }), el('span', { text: money(vat, f.currency.value) + lv(vat) })));
    totalsBox.appendChild(el('div', { class: 'line grand' }, el('span', { text: 'Общо' }), el('span', { text: money(t.total, f.currency.value) + lv(t.total) })));
  }
  const artDatalist = el('datalist', { id: 'inv-art-dl' });
  const recvDatalist = el('datalist', { id: 'inv-recv-dl' });
  const receivedInput = input({ value: inv.received_by || '' });
  receivedInput.setAttribute('list', 'inv-recv-dl');
  receivedInput.addEventListener('focus', function(){ clear(recvDatalist); var _rs={}; (invoicesCache()||[]).forEach(function(iv){ var rb=String(iv.received_by||'').trim(); if(rb && !_rs[rb]){ _rs[rb]=1; recvDatalist.appendChild(el('option',{value:rb})); } }); });
  var _artMap = {};
  function rebuildArt(){ _artMap = {}; clear(artDatalist); (invoicesCache()||[]).forEach(function(iv){ if(iv.company_id!==state().activeCompany) return; (iv.items||[]).forEach(function(it2){ var dd=String(it2.description||'').trim(); if(dd && _artMap[dd]==null){ _artMap[dd]=Number(it2.unit_price)||0; artDatalist.appendChild(el('option',{value:dd})); } }); }); }
  // (Б.4) Двата „селекта-действия" станаха изрични бутони с избор в малък
  // прозорец. Добавянето е ОТДОЛУ — въведеното на ръка не се губи (старият
  // селект за копиране заменяше целия списък мигновено), затова и въпрос
  // не трябва. Единственият ПРАЗЕН начален ред се заменя — иначе всяко
  // копие започва с празен ред отгоре.
  function appendItems(newOnes) {
    if (items.length === 1 && !String(items[0].description || '').trim() && (!items[0].unit_price || Number(items[0].unit_price) === 0)) items = [];
    items = items.concat(newOnes);
    drawItems(); recompute();
    // Програмното добавяне е незаписана работа: бележи формата (А.1) —
    // иначе Esc след копиране би изхвърлил редовете без въпрос.
    itemsBody.dispatchEvent(new Event('input', { bubbles: true }));
  }
  // Общият прозорец за избор: търсачка + списък от бутони-редове.
  function pickDialog({ title, draw, foot }) {
    const listBox = el('div', { class: 'pick-list' });
    const body2 = el('div', {},
      searchInput({ placeholder: 'Търсене…', onSearch: (q) => draw(listBox, q) }),
      listBox);
    draw(listBox, '');
    openModal({ title, body: body2, foot });
  }
  const copyBtn = el('button', { class: 'btn sm ghost', type: 'button', text: 'Копирай от стара фактура', onClick: () => {
    const list = (invoicesCache() || []).filter((iv) => iv.company_id === state().activeCompany && iv.items && iv.items.length).sort((a, b) => (b.seq || 0) - (a.seq || 0)).slice(0, 150);
    pickDialog({
      title: 'Копирай редове от стара фактура',
      foot: el('div', {}, el('button', { class: 'btn ghost', text: 'Отказ', onClick: closeModal })),
      draw: (box, q) => {
        clear(box);
        const hits = list.filter((iv) => { if (!q) return true; const c = (contacts || []).find((x) => x.id === iv.contact_id); return ((iv.number || '') + ' ' + ((c && c.name) || '')).toLowerCase().includes(q); });
        if (!hits.length) box.appendChild(el('div', { class: 'menu-empty', text: 'Няма съвпадение.' }));
        hits.forEach((iv) => {
          const c = (contacts || []).find((x) => x.id === iv.contact_id);
          box.appendChild(el('button', { class: 'pick-row', type: 'button', onClick: () => {
            appendItems(iv.items.map(copyItem));
            closeModal();
            toast('Добавени ' + iv.items.length + ' реда от фактура № ' + iv.number + '.');
          } },
            el('span', { style: 'font-weight:600', text: '№ ' + iv.number }),
            el('span', { text: (c && c.name) || '' }),
            el('span', { style: 'color:var(--muted);margin-left:auto', text: dfmt(iv.issue_date) + ' · ' + iv.items.length + ' реда' })));
        });
      },
    });
  } });
  // „Приспадни аванс": само в обикновена (не авансова) фактура и само ако
  // клиентът има авансова фактура с остатък — иначе бутонът не се вижда.
  const openAdv = () => advances.openAdvances((invoicesCache() || []).filter((iv) => iv.company_id === state().activeCompany), f.contact_id.value, existing && existing.id);
  const advBtn = el('button', { class: 'btn sm ghost', type: 'button', text: 'Приспадни аванс', onClick: () => {
    const list = openAdv();
    pickDialog({
      title: 'Приспадни аванс',
      foot: el('div', {}, el('button', { class: 'btn ghost', text: 'Отказ', onClick: closeModal })),
      draw: (box, q) => {
        clear(box);
        const hits = list.filter((x) => !q || String(x.invoice.number || '').includes(q));
        if (!hits.length) box.appendChild(el('div', { class: 'menu-empty', text: 'Няма авансови фактури с остатък.' }));
        hits.forEach((x) => box.appendChild(el('button', { class: 'pick-row', type: 'button', onClick: () => {
          // Ред с минус: основата и ДДС-ът на окончателната са след
          // приспадането; сумата може да се намали за частично приспадане.
          appendItems([{ description: 'Приспадане на аванс по фактура № ' + x.invoice.number + ' от ' + dfmt(x.invoice.issue_date), quantity: 1, unit_price: -x.available, advance_of: x.invoice.id }]);
          closeModal();
          toast('Приспаднат аванс ' + money(x.available, f.currency.value) + ' по фактура № ' + x.invoice.number + '.');
        } },
          el('span', { style: 'font-weight:600', text: '№ ' + x.invoice.number }),
          el('span', { text: dfmt(x.invoice.issue_date) }),
          el('span', { style: 'color:var(--muted);margin-left:auto', text: 'остават ' + money(x.available, f.currency.value) }))));
      },
    });
  } });
  function syncAdvBtn() {
    advBtn.style.display = (!PF && docTypeSel.value === 'invoice' && !advanceCb.checked && openAdv().length) ? '' : 'none';
  }
  _syncAdv = syncAdvBtn;
  syncAdvBtn();
  f.contact_id.addEventListener('change', syncAdvBtn);
  // Авансовата фактура отива по Кт 412 — видът приход на редовете ѝ е без
  // значение, затова менюто му се скрива (drawItems).
  advanceCb.addEventListener('change', () => { syncAdvBtn(); drawItems(); });
  var _cat = [];
  api('/articles'+cq()).then(function(list){ _cat = list || []; _hints.forEach((fn) => fn()); }).catch(function(){});
  // Ценовите листи (т. 5 a): листата върви с клиента — цената на артикул от
  // каталога идва по нея; под реда пише каква е цената по листата.
  let _lists = [];
  api('/price-lists' + cq()).then((list) => { _lists = Array.isArray(list) ? list : []; _hints.forEach((fn) => fn()); }).catch(() => {});
  const _client = () => (contacts || []).find((c) => String(c.id) === String(f.contact_id.value)) || null;
  const _docDate = () => (f.issue_date.dataset && f.issue_date.dataset.iso) || localTodayIso();
  const listPrice = (a) => listPriceFor(_lists, _client(), a, _docDate());
  // Склад (ПРОМТ-8): „налични: 12 бр." до складовите редове. Наличностите се
  // зареждат ВЕДНЪЖ при отваряне; без право „Склад" подсказка просто няма —
  // истинската проверка (warn/block) е на сървъра при запис.
  let _stock = null;
  const _hints = [];
  const _co = activeCompanyObj();
  // Без изписване от фактурите (ресторант, ПРОМТ-16) „налични" и недостиг
  // само биха плашили — документът не мърда склада.
  // Проформата не изписва, но показва наличното (грешка от картата
  // „проформата не показва наличности") — на всяка фирма със склад.
  if (stockTypes(_co && _co.warehouse_mode).length && (PF || invoiceMovesStock(_co))) {
    api('/stock/balances' + cq()).then((rows) => {
      _stock = {};
      (Array.isArray(rows) ? rows : []).forEach((r) => { _stock[r.article_id] = r; });
      _hints.forEach((fn) => fn());
    }).catch(() => {});
  }
  const _baseQty = (it) => {
    const a = (_cat || []).find((x) => Number(x.id) === Number(it.article_id));
    const u = String(it.unit || '').trim();
    let factor = 1;
    if (a && u && u !== a.measure) {
      const cv = (a.conversions || []).find((c) => String(c.unit || '').trim() === u);
      factor = cv ? Number(cv.factor) || 1 : 1;
    }
    return (Number(it.quantity) || 0) * factor;
  };
  // При редакция своето вече изписано количество е „налично" за документа
  // (сървърът го сторнира преди проверката).
  // Смята се при всяко обновяване: каталогът (преобразуванията) идва асинхронно.
  const _own = (articleId) => {
    if (!(existing && existing.id) || existing.doc_type === 'credit') return 0;
    return (existing.items || []).filter((x) => Number(x.article_id) === Number(articleId) && Number(x.quantity) > 0).reduce((s2, x) => s2 + _baseQty(x), 0);
  };
  function stockHint(it) {
    const h = el('div', { class: 'stock-hint', 'aria-live': 'polite' });
    const upd = () => {
      const b = _stock && it.article_id != null ? _stock[it.article_id] : null;
      // ДИ без „изписва стока" не пипа склада — наличността е без значение.
      const noMove = docTypeSel && docTypeSel.value === 'debit' && !debitMoves.checked;
      if (!b || noMove) { h.textContent = ''; h.style.display = 'none'; h.classList.remove('sev-warn'); return; }
      h.style.display = '';
      const fq = (n) => String(Math.round(n * 10000) / 10000);
      const avail = (Number(b.qty) || 0) + _own(it.article_id);
      const credit = docTypeSel && docTypeSel.value === 'credit';
      const need = credit ? 0 : items.filter((x) => Number(x.article_id) === Number(it.article_id) && Number(x.quantity) > 0).reduce((s2, x) => s2 + _baseQty(x), 0);
      const short = need > avail + 1e-9;
      h.classList.toggle('sev-warn', short);
      h.textContent = short
        ? 'Недостиг: налични ' + fq(avail) + ' ' + b.measure + ', по документа ' + fq(need) + ' ' + b.measure + '.'
        : 'налични: ' + fq(avail) + ' ' + b.measure;
    };
    _hints.push(upd);
    upd();
    return h;
  }
  // „Последно на този клиент: 12,50 € / бр. (ф-ра № … от …)" — само за
  // сведение под реда (Промени 16.10, т. 5а). Смята се при всяка смяна на
  // клиента, артикула или описанието; без клиент или без история — нищо.
  function lastPriceHint(it) {
    const h = el('div', { class: 'last-price-hint', 'aria-live': 'polite' });
    const upd = () => {
      const lp = lastPriceFor(invoicesCache() || [], f.contact_id.value, it, existing && existing.id);
      const a = it.article_id != null && it.article_id !== '' ? (_cat || []).find((x) => Number(x.id) === Number(it.article_id)) : null;
      const txt = [a ? listPriceText(listPrice(a)) : '', lastPriceText(lp)].filter(Boolean).join(' · ');
      h.textContent = txt;
      h.style.display = txt ? '' : 'none';
    };
    _hints.push(upd);
    upd();
    return h;
  }
  const catBtn = el('button', { class: 'btn sm ghost', type: 'button', text: 'Добави артикул от каталога', onClick: () => {
    pickDialog({
      title: 'Добави артикул от каталога',
      // Остава отворен: често се добавят няколко артикула подред.
      foot: el('div', {}, el('button', { class: 'btn', text: 'Готово', onClick: closeModal })),
      draw: (box, q) => {
        clear(box);
        // Търси и по кода на артикула (Макс, 20.10.2026).
        const hits = (_cat || []).filter((a) => !q || String(a.name || '').toLowerCase().includes(q) || String(a.code || '').toLowerCase().includes(q));
        if (!_cat.length) box.appendChild(el('div', { class: 'menu-empty', text: 'Каталогът е празен. Добавете артикули в раздел „Артикули".' }));
        else if (!hits.length) box.appendChild(el('div', { class: 'menu-empty', text: 'Няма съвпадение.' }));
        hits.forEach((a) => {
          box.appendChild(el('button', { class: 'pick-row', type: 'button', onClick: () => {
            // article_id свързва реда със склада/Конто; unit е базовата мярка
            // на артикула (сървърът проверява, че артикулът е от фирмата).
            // Цената — по листата на клиента, ако има; _auto: при смяна на
            // клиента редът минава на неговата цена (ръчно пипнатата остава).
            const lp2 = listPrice(a);
            appendItems([{ description: a.name, quantity: 1, unit_price: lp2 ? lp2.price : (Number(a.unit_price) || 0), article_id: a.id, unit: a.measure || '', code: a.code || '', _auto: true }]);
            toast('Добавен: ' + a.name + '.');
          } },
            a.code ? el('span', { class: 'art-code', text: a.code }) : null,
            el('span', { text: a.name }),
            // Колко има в склада — още при избора (Макс, 20.10.2026).
            stockQtyText(a, _stock, _co && _co.warehouse_mode) ? el('span', { class: 'pick-stock', text: stockQtyText(a, _stock, _co && _co.warehouse_mode) }) : null,
            el('span', { style: 'color:var(--muted);margin-left:auto', text: money(Number(a.unit_price) || 0, 'EUR') })));
        });
      },
    });
  } });
  // Менюто „Приход за Конто" под описанието на реда (вж. _revs горе).
  // „Артикули по листа …" (Макс, 20.10.2026): когато клиентът има ценова
  // листа, бутонът до „Добави артикул от каталога" показва САМО артикулите с
  // цена по нея — със самата цена. Листа само с процент дава целия каталог
  // с отстъпката. Без листа (или изтекла към датата) бутонът го няма.
  const clientList = () => {
    const c = _client();
    if (!c || c.price_list_id == null || c.price_list_id === '') return null;
    return (_lists || []).find((l) => Number(l.id) === Number(c.price_list_id)) || null;
  };
  const listArticles = () => (_cat || []).map((a) => ({ a, r: listPrice(a) })).filter((x) => x.r)
    .sort((x, y) => (x.r.how === 'price' ? 0 : 1) - (y.r.how === 'price' ? 0 : 1) || String(x.a.name || '').localeCompare(String(y.a.name || ''), 'bg'));
  const listBtn = el('button', { class: 'btn sm ghost', type: 'button', style: 'display:none', text: 'Артикули по листа', onClick: () => {
    const l = clientList();
    pickDialog({
      title: 'Артикули по ценовата листа „' + ((l && l.name) || '') + '“',
      foot: el('div', {}, el('button', { class: 'btn', text: 'Готово', onClick: closeModal })),
      draw: (box, q) => {
        clear(box);
        const all = listArticles();
        const hits = all.filter((x) => !q || String(x.a.name || '').toLowerCase().includes(q) || String(x.a.code || '').toLowerCase().includes(q));
        if (!all.length) box.appendChild(el('div', { class: 'menu-empty', text: 'В листата на клиента няма артикули с цена към датата на документа.' }));
        else if (!hits.length) box.appendChild(el('div', { class: 'menu-empty', text: 'Няма съвпадение.' }));
        hits.forEach(({ a, r }) => {
          const q2 = stockQtyText(a, _stock, _co && _co.warehouse_mode);
          box.appendChild(el('button', { class: 'pick-row', type: 'button', onClick: () => {
            appendItems([{ description: a.name, quantity: 1, unit_price: r.price, article_id: a.id, unit: a.measure || '', code: a.code || '', _auto: true }]);
            toast('Добавен: ' + a.name + ' — ' + money(r.price, 'EUR') + ' по листата.');
          } },
            a.code ? el('span', { class: 'art-code', text: a.code }) : null,
            el('span', { text: a.name }),
            q2 ? el('span', { class: 'pick-stock', text: q2 }) : null,
            el('span', { style: 'margin-left:auto;text-align:right' },
              el('span', { style: 'font-weight:600', text: money(r.price, 'EUR') }),
              r.how === 'pct' ? el('span', { class: 'pick-stock', text: '−' + String(r.pct).replace('.', ',') + ' %' }) : null,
              Number(a.unit_price) && Number(a.unit_price) !== r.price ? el('span', { class: 'pick-stock', style: 'text-decoration:line-through', text: money(Number(a.unit_price), 'EUR') }) : null)));
        });
      },
    });
  } });
  const syncListBtn = () => {
    const l = clientList();
    const show = !!l && listArticles().length > 0;
    listBtn.style.display = show ? '' : 'none';
    if (l) listBtn.textContent = 'Артикули по листа „' + (l.name || '') + '“';
  };
  _hints.push(syncListBtn);
  f.contact_id.addEventListener('change', syncListBtn);
  f.issue_date.addEventListener('change', syncListBtn);
  syncListBtn();

  function revenueSelect(it, rowNo) {
    // Приспаднат аванс и редовете на авансова фактура са по 412, не по приход.
    if (!_revs || it.advance_of != null || advanceCb.checked) return null;
    if (!_revs.some((r) => r.key === it.revenue)) it.revenue = _revs[0].key;
    const s = el('select', { class: 'rev-sel', 'aria-label': 'Приход за Конто, ' + rowNo, onChange: (e) => { it.revenue = e.target.value; } },
      ..._revs.map((r) => el('option', { value: r.key, text: _isClient ? r.name : (r.name + ' — ' + r.account) })));
    s.value = it.revenue;
    // Видим надпис пред менюто — клиентът няма как да се сети какво е.
    return el('div', { class: 'rev-line' }, el('span', { class: 'rev-label', text: 'Вид приход' }), s);
  }
  // Кодът на артикула над описанието (Макс, 20.10.2026) — същият, който
  // излиза при печат. Ред без артикул или артикул без код — нищо.
  function codeHint(it) {
    const h = el('div', { class: 'art-code' });
    const upd = () => {
      const linked = it.article_id != null && it.article_id !== '';
      const a = linked ? (_cat || []).find((x) => Number(x.id) === Number(it.article_id)) : null;
      const code = linked ? ((a && a.code) || it.code || '') : '';
      h.textContent = code ? 'Код: ' + code : '';
      h.style.display = code ? '' : 'none';
    };
    _hints.push(upd);
    upd();
    return h;
  }
  // Ръчно пипнато описание развързва реда от артикула (delete в onInput по-
  // долу): „Кафе" с article_id на „Захар" би изписало грешна стока от склада.
  function drawItems() {
    rebuildArt();
    clear(itemsBody);
    _hints.length = 0;
    // Бутонът „Артикули по листа“ не е ред — оцелява при прерисуването.
    _hints.push(syncListBtn);
    items.forEach((it, idx) => {
      // Полетата в реда не са свързани с <th> — без имена четецът казваше
      // „поле за въвеждане, празно" три пъти на ред. Номерът на реда влиза в
      // името, за да се различават редовете един от друг.
      const rowNo = 'ред ' + (idx + 1);
      const desc = input({ value: it.description, placeholder: 'Описание', 'aria-label': 'Описание, ' + rowNo, onInput: (e) => { it.description = e.target.value; delete it.article_id; delete it.code; if (String(e.target.value || '').trim()) e.target.classList.remove('bad'); var _v = String(e.target.value||'').trim(); if (_artMap[_v] != null && (!it.unit_price || Number(it.unit_price) === 0)) { it.unit_price = _artMap[_v]; price.value = _artMap[_v]; updLine(); recompute(); } _hints.forEach((fn) => fn()); } }); desc.setAttribute('list','inv-art-dl');
      const qty = input({ type: 'number', step: '0.01', value: it.quantity, 'aria-label': 'Количество, ' + rowNo, onInput: (e) => { it.quantity = e.target.value; updLine(); recompute(); _hints.forEach((fn) => fn()); } });
      // Сумата на реда (стъпка 7): грешно количество личи на СВОЯ ред, не чак
      // в „Общо". Смята се със същата функция като сървъра — редовете се
      // събират точно до данъчната основа, без стотинка разлика.
      const lineTotal = el('td', { class: 'num line-total', style: 'width:110px;white-space:nowrap', 'data-label': 'Сума' });
      const updLine = () => { lineTotal.textContent = money(computeTotals([it], 0).subtotal, f.currency.value); };
      updLine();
      // „без ДДС": ДДС-то се добавя ОТГОРЕ (services/totals.js) — цена с ДДС
      // тук значи фактура с 20 % над уговореното.
      const price = input({ type: 'number', step: '0.01', value: it.unit_price, 'aria-label': 'Единична цена без ДДС, ' + rowNo, onInput: (e) => { it.unit_price = e.target.value; delete it._auto; updLine(); recompute(); } });
      // Мерната единица на реда (Промени 16.10, т. 3й): от каталога идва
      // мярката на артикула; при преобразуване може да е и „кашон" — складът
      // смята по нея. Свободен ред може и без мярка (услуга).
      const unit = input({ value: it.unit || '', placeholder: it.article_id != null ? 'мярка' : '—', list: 'inv-unit-dl', maxlength: '30', 'aria-label': 'Мярка, ' + rowNo,
        onInput: (e) => { it.unit = e.target.value.trim(); if (it.unit) e.target.classList.remove('bad'); _hints.forEach((fn) => fn()); } });
      itemsBody.appendChild(el('tr', {},
        // data-label: на телефон заглавният ред е скрит и редът е карта —
        // надписът стои над стойността (CSS .inv-items, под 640 px).
        el('td', {}, codeHint(it), desc, stockHint(it), lastPriceHint(it), revenueSelect(it, rowNo)),
        el('td', { style: 'width:96px', 'data-label': 'Мярка' }, unit),
        el('td', { class: 'num', style: 'width:80px', 'data-label': 'Кол.' }, qty),
        el('td', { class: 'num', style: 'width:110px', 'data-label': 'Ед. цена без ДДС' }, price),
        lineTotal,
        el('td', { style: 'width:30px' }, el('button', { class: 'btn sm danger', text: '×', 'aria-label': 'Изтрий ' + rowNo, onClick: () => { items.splice(idx, 1); if (!items.length) items.push({ description: '', quantity: 1, unit_price: 0 }); drawItems(); recompute(); } }))
      ));
    });
  }
  drawItems(); recompute();
  f.vat_rate.addEventListener('input', recompute);
  // Основанието е задължително при 0 % (сървърът отказва без него) —
  // подсказката стои под полето, докато ставката е 0 и нищо не е избрано.
  const basisHint = el('div', { class: 'cal-hint', style: 'padding:0 0 8px', text: 'При ДДС 0 % фактурата трябва да посочва основание за неначисляване — изберете го от менюто (или „Друго" и го напишете).' });
  const basisValue = () => (f.vat_basis.value === BASIS_OTHER ? f.vat_basis_other.value : f.vat_basis.value).trim();
  const basisMissing = () => (Number(f.vat_rate.value) || 0) === 0 && !basisValue();
  // Основанието е задължително само във фактурата — проформата не е данъчен документ.
  const syncBasisHint = () => { basisHint.style.display = (!PF && basisMissing()) ? '' : 'none'; };
  // Нерегистрираната фирма (ставката е заключена на 0) има едно основание,
  // каквото и да е избрано като режим.
  const basisCtx = () => (f.vat_rate.disabled ? 'not_registered' : f.tax_treatment.value);
  // Менюто се строи наново при всяка смяна на режима — вижда се само
  // свързаното с него, плюс винаги „Друго".
  function drawBasisMenu() {
    clear(f.vat_basis);
    f.vat_basis.appendChild(el('option', { value: '', text: '— изберете основание —' }));
    basesFor(basisCtx()).forEach((g) => {
      f.vat_basis.appendChild(el('optgroup', { label: g.group }, ...g.items.map((it) => el('option', { value: it.value, text: it.label }))));
    });
    f.vat_basis.appendChild(el('optgroup', { label: 'Друго' }, el('option', { value: BASIS_OTHER, text: OTHER_LABEL })));
  }
  const inMenu = (v) => !!v && [...f.vat_basis.options].some((o) => o.value === v);
  // Текст → меню. Избира се само ТОЧНО съвпадение с ред от менюто; всичко
  // друго (стара ръчна фактура, „чл. 45 ЗДДС") отива в „Друго" непокътнато
  // — иначе само отварянето и записът биха подменили напечатания текст.
  function setBasis(text) {
    const t = String(text || '').trim();
    const hit = findBasis(t);
    if (!t) { f.vat_basis.value = ''; f.vat_basis_other.value = ''; }
    else if (hit && hit.value === t && inMenu(t)) { f.vat_basis.value = t; f.vat_basis_other.value = ''; }
    else { f.vat_basis.value = BASIS_OTHER; f.vat_basis_other.value = t; }
    syncBasis();
  }
  // Полето се вижда само при 0 % — при 20 %/9 % основание не се печата.
  // Стойността остава, ако ставката се върне на 0.
  let basisField = null, basisOtherField = null;
  function syncBasis() {
    const zero = (Number(f.vat_rate.value) || 0) === 0;
    if (basisField) basisField.style.display = zero ? '' : 'none';
    if (basisOtherField) basisOtherField.style.display = zero && f.vat_basis.value === BASIS_OTHER ? '' : 'none';
    syncBasisHint();
  }
  // Предложеното от режима основание може да се подмени при следваща смяна
  // на режима; избраното на ръка — никога. Ако избраното го няма в новото
  // меню, се изчиства — освен ако е написано в „Друго".
  let _basisAuto = '';
  // ВОД, обратно начисляване и освободена — без ДДС (Макс, 02.11.2026: при
  // ВОД се начисляваше 20 %). Режимът сваля ставката на 0 и я държи там;
  // сървърът отказва запис с ДДС при тези режими (services/docItems.js).
  const ZERO_VAT = ['eu_b2b', 'reverse_charge', 'exempt'];
  const zeroRegime = () => ZERO_VAT.includes(f.tax_treatment.value);
  const zeroNote = el('div', { class: 'cal-hint', style: 'padding:0 0 8px' });
  function syncZeroVat(fromRegime) {
    if (!zeroRegime()) { zeroNote.style.display = 'none'; return; }
    const label = (TAX_TREATMENT_OPTS.find(([v]) => v === f.tax_treatment.value) || [])[1] || '';
    zeroNote.textContent = 'При „' + label + '" ДДС не се начислява — ставката е 0 % и основанието излиза във фактурата.';
    zeroNote.style.display = '';
    if ((Number(f.vat_rate.value) || 0) !== 0) {
      f.vat_rate.value = '0';
      if (!fromRegime) toast('При ' + label + ' ДДС не се начислява — ставката остава 0 %.', true);
      recompute();
      syncBasis();
    }
  }
  f.vat_rate.addEventListener('change', () => syncZeroVat(false));
  f.tax_treatment.addEventListener('change', () => syncZeroVat(true));
  f.tax_treatment.addEventListener('change', () => {
    const wasOther = f.vat_basis.value === BASIS_OTHER;
    const cur = basisValue();
    drawBasisMenu();
    const sug = suggestedBasis(basisCtx());
    if (!cur || (!wasOther && cur === _basisAuto)) { setBasis(sug); _basisAuto = sug; }
    else if (wasOther) setBasis(f.vat_basis_other.value);
    else if (inMenu(cur)) setBasis(cur);
    else setBasis('');
    if (!basisValue() && f.tax_treatment.value === 'exempt') f.vat_basis.focus();
  });
  f.vat_basis.addEventListener('change', () => {
    syncBasis();
    if (f.vat_basis.value === BASIS_OTHER) f.vat_basis_other.focus();
  });
  f.vat_basis_other.addEventListener('input', syncBasisHint);
  f.vat_rate.addEventListener('input', syncBasis);
  f.vat_rate.addEventListener('change', syncBasis);
  // Падежът следва издаването, ДОКАТО не бъде пипнат на ръка — тогава
  // ръчната стойност е по-силна и не се пренаписва (Б.5).
  let _dueTouched = PF || !!(inv.due_date);
  f.due_date.addEventListener('input', () => { _dueTouched = true; });
  f.issue_date.addEventListener('input', () => {
    if (_dueTouched) return;
    const iso = plusDays((f.issue_date.dataset && f.issue_date.dataset.iso) || '', dueDaysOf(activeCompanyObj()));
    if (!iso) return;
    f.due_date.value = isoToBg(iso);
    f.due_date.dataset.iso = iso;
  });
  // „Само евро" (01.09.2026): менюто за валута го няма. Заварен документ в
  // друга валута я пази — бележката казва защо сумите не са в евро (Г.7:
  // необяснената разлика минава за повреда).
  const curNote = (inv.currency && inv.currency !== 'EUR')
    ? el('div', { class: 'cal-hint', style: 'padding:0 0 8px', text: 'Заварен документ във валута ' + inv.currency + ' — запазва се както е издаден.' })
    : null;
  // Смяната на датата мести документа спрямо границата 01.09.2026 —
  // тоталите се преизчисляват, за да покажат/скрият левовете.
  f.issue_date.addEventListener('change', recompute);
  f.issue_date.addEventListener('input', recompute);
  recompute();
  const numPreview = el('div', { style: 'font-size:13px;color:var(--accent,#0f766e);font-weight:600;margin:-2px 0 10px' });
  // (Г.7) Фирма без ДДС регистрация: ставката е 0 и заключена — с бележка,
  // иначе „ДДС % = 0" минава за повреда.
  const vatNote = el('div', { class: 'cal-hint', style: 'padding:0 0 8px', text: 'Фирмата няма ДДС регистрация — ' + (PF ? 'проформите' : 'фактурите') + ' са без ДДС.' });
  vatNote.style.display = 'none';
  if (!(existing && existing.id)) { const reg = !!String((_company && _company.vat_number) || '').trim(); if (!reg) { f.vat_rate.value = '0'; f.vat_rate.disabled = true; vatNote.style.display = ''; recompute(); } }
  // Заварен документ с ВОД/обратно/освободена и 20 % — вижда се веднага какво
  // не е наред (ставката става 0, бележката обяснява; записът минава само с 0).
  syncZeroVat(!(existing && existing.id));
  // Менюто — след ставката: нерегистрираната фирма има свое меню. Новата
  // фактура без основание получава предложеното за режима; копие и заварен
  // документ показват своето както е (предложението не го подменя).
  drawBasisMenu();
  setBasis(inv.vat_basis);
  if (!basisValue() && !(existing && existing.id)) { const sug = suggestedBasis(basisCtx()); if (sug) { setBasis(sug); _basisAuto = sug; } }
  // Фактурникът, който заминава при „Запази" (book_start) — празно = първият.
  function _bookStart() { return bookSel ? bookSel.value : (_books[0] ? _books[0].start : ''); }
  // Фактурата — като проформата (ОТЧЕТ-ПРОМТ-38, Д1): номерът идва от
  // сървъра (GET /invoices/next-number), от СЪЩАТА функция, която го дава при
  // записа. Екранът броеше свое по числов диапазон, а броячът брои само
  // фактурите на същия фактурник (book_start) — при заварени фактури отпреди
  // фактурниците формата обещаваше № 0001000004, а записът получаваше 0000000006.
  let _invNext = '';
  let _invAsk = 0;
  function askInvoiceNumber() {
    if (PF || (existing && existing.id)) return;
    const ask = ++_invAsk;
    _invNext = '';
    updatePreview();
    api('/invoices/next-number?company_id=' + encodeURIComponent(state().activeCompany || '') + '&book=' + encodeURIComponent(_bookStart()))
      // Отговор за предишен фактурник (бърза смяна) не бива да изпише чужд номер.
      .then((r) => { if (ask !== _invAsk) return; _invNext = (r && r.number) || ''; updatePreview(); }).catch(() => {});
  }
  // Проформата: номерът идва от сървъра (GET /proformas/next-number) — от
  // СЪЩАТА функция, която го дава при записа (началото и представката на фирмата).
  let _pfNext = '';
  if (PF && !(existing && existing.id)) {
    api('/proformas/next-number?company_id=' + encodeURIComponent(state().activeCompany || '')).then((r) => { _pfNext = (r && r.number) || ''; updatePreview(); }).catch(() => {});
  }
  function updatePreview() {
    if (existing && existing.id) numPreview.textContent = 'Номер на ' + (PF ? 'проформата' : 'фактурата') + ': № ' + existing.number;
    else if (PF) numPreview.textContent = _pfNext ? '➜ Ще бъде издадена проформа № ' + _pfNext : '➜ Новата проформа ще получи следващия свободен номер.';
    else numPreview.textContent = _invNext ? '➜ Ще бъде издадена фактура № ' + _invNext : '➜ Новата фактура ще получи следващия свободен номер.';
  }
  updatePreview();
  askInvoiceNumber();
  // Всеки фактурник има свой брояч — при смяна номерът се пита наново.
  if (bookSel) bookSel.addEventListener('change', askInvoiceNumber);
  // Новата фактура (и отворената отвън — табло, контакт — без кеш) зарежда
  // фактурите на фирмата: за номера и за „последната цена" до редовете.
  const _cached = invoicesCache();
  if (!(existing && existing.id) || !(Array.isArray(_cached) && _cached.length)) { api('/invoices' + cq()).then((r) => { if (Array.isArray(r)) setInvoices(r); updatePreview(); _hints.forEach((fn) => fn()); }).catch(() => {}); }

  f.contact_id.style.display = 'none';
  f.contact_id.setAttribute('aria-hidden', 'true');
  f.contact_id.tabIndex = -1;
  const clientBox = clientCombobox({ contacts, hidden: f.contact_id, companyId: state().activeCompany, onNew: (name) => quickNewClient(name) });
  // Четем картона при всяка смяна, за да не показваме реквизити на друг клиент.
  // Самата фактура продължава да пази contact_id, без втори източник на данни.
  const clientData = el('dl', { class: 'inv-client-data', 'aria-live': 'polite' });
  function showClientData() {
    clear(clientData);
    const c = contacts.find(x => String(x.id) === String(f.contact_id.value));
    if (!c) { clientData.appendChild(el('div', { class: 'inv-client-empty', text: 'Изберете клиент, за да видите реквизитите му.' })); return; }
    const entries = [['ЕИК / Булстат', c.eik], ['ДДС №', c.vat_number], ['МОЛ', c.mol], ['Имейл', c.email], ['Адрес', [c.city, c.address].filter(Boolean).join(', ')], ['Държава', c.country || 'BG']];
    entries.forEach(([label, value]) => clientData.appendChild(el('div', { class: label === 'Адрес' ? 'inv-client-address' : '' }, el('dt', { text: label }), el('dd', { text: value || 'Не е попълнено' }))));
  }
  f.contact_id.addEventListener('change', showClientData);
  showClientData();

  const itemsTable = el('table', { class: 'items inv-items' }, el('thead', {}, el('tr', {}, el('th', { text: 'Описание' }), el('th', { text: 'Мярка' }), el('th', { class: 'num', text: 'Кол.' }), el('th', { class: 'num', text: 'Ед. цена без ДДС' }), el('th', { class: 'num', text: 'Сума' }), el('th'))), itemsBody);
  // Подсказките „последна цена" следват избрания клиент.
  f.contact_id.addEventListener('change', () => _hints.forEach((fn) => fn()));
  // Нов клиент → цените от каталога минават по неговата листа.
  f.contact_id.addEventListener('change', () => {
    if (repriceAuto(items, _cat, _lists, _client(), _docDate())) { drawItems(); recompute(); toast('Цените на артикулите са по листата на клиента.'); }
  });

  const recurringExtra = el('div', { style: 'display:none;margin-top:8px' }, row(field('Период', f.recurring_interval, { size: 'm' }), field('Следваща дата', f.recurring_next, { size: 's' })),
    // Последният пропуск при издаване (склад „Забранявай" и недостиг).
    (inv.recurring_skipped && inv.recurring_skipped.text) ? el('div', { class: 'sev-warn', role: 'status', text: inv.recurring_skipped.text }) : null);
  f.recurring.addEventListener('change', () => { recurringExtra.style.display = f.recurring.checked ? 'block' : 'none'; });
  if (inv.recurring) recurringExtra.style.display = 'block';

  basisField = field('Основание за неначисляване на ДДС', f.vat_basis, { size: 'm' });
  basisOtherField = field('Основание — свой текст', f.vat_basis_other, { size: 'm' });
  syncBasis();
  const body = el('div', { class: 'invoice-editor' },
    bookSel ? field('Фактурник', bookSel, { size: 'm' }) : null,
    (locked ? el('div', { style: 'background:#fef3c7;border:1px solid #fde68a;color:#92400e;border-radius:8px;padding:10px 12px;margin-bottom:10px;font-size:13px', text: PF
      ? '🔒 Заключена при издаване на фактура на ' + dfmt(inv.locked_at) + '. Проформата е само за четене.'
      : '🔒 Заключена при експорт към Конто на ' + dfmt(inv.konto_exported_at) + '. Съдържанието е само за четене — отключва го администратор, с причина. Статусът (изпратена/платена/просрочена) може да се сменя.' }) : null),
    numPreview,
    PF ? null : row(field('Тип документ', docTypeSel, { size: 'm' }), refField),
    PF ? null : debitField,
    PF ? null : advanceField,
    // „Провери в VIES" проверява КЛИЕНТА — затова е до него (стъпка 7), а
    // не самотен ред между клиента и датите.
    // Клиентът (ПРОМТ-19, т. 2): търсачката е ПЪРВА — field() връзва етикета
    // към първото поле; скритият select пази избора за записа и за VIES.
    el('section', { class: 'invoice-client' }, row(field('Клиент', el('div', { style: 'display:flex;gap:6px;align-items:flex-start;flex-wrap:wrap' }, clientBox, f.contact_id,
      el('button', { class: 'btn sm ghost', type: 'button', text: '+ Нов клиент', title: 'Създава клиент, без да губите ' + (PF ? 'проформата' : 'фактурата'), onClick: () => quickNewClient() }),
      viesButton(() => { const cc = contacts.find((x) => String(x.id) === String(f.contact_id.value)); return cc ? (cc.vat_number || cc.eik) : ''; })))), clientData),
    f.currency, curNote,
    row(field('Дата на издаване', f.issue_date, { size: 's' }), field(PF ? 'Валидна до' : 'Падеж', f.due_date, { size: 's' })),
    el('h4', { text: 'Стоки и услуги', class: 'invoice-section-title' }),
    itemsTable,
    canEdit ? el('button', { class: 'btn sm ghost', text: '+ Нов ред', onClick: () => { items.push({ description: '', quantity: 1, unit_price: 0 }); drawItems(); } }) : null,
    canEdit ? el('div', { style: 'margin:8px 0;display:flex;align-items:center;gap:8px;flex-wrap:wrap' }, copyBtn, catBtn, listBtn, advBtn) : null, artDatalist, unitDatalist('inv-unit-dl'),
    field('ДДС %', f.vat_rate, { size: 's' }),
    vatNote, zeroNote,
    row(field('Данъчен режим', f.tax_treatment, { size: 'm' }), basisField),
    basisOtherField,
    basisHint,
    PF ? null : row(field('Съставител', composerSel, { size: 'm' }), field('Получил', receivedInput, { size: 'm' })), PF ? null : recvDatalist,
    field('Основание / бележки', f.notes),
    // „Повтаряща се" се показва само на фактура, която ВЕЧЕ е такава — за да
    // може да се изключи (Промени 16.10, т. 3в: функцията не се ползва и
    // само пречеше в новата фактура).
    !PF && inv.recurring ? el('div', { class: 'field' }, el('label', { class: 'choice' }, f.recurring, el('span', { text: 'Повтаряща се фактура (издава се от „Действия → Издай повтарящите се")' }))) : null,
    !PF && inv.recurring ? recurringExtra : null,
    el('section', { class: 'invoice-settlement' },
      el('div', { class: 'invoice-payment' }, el('h4', { class: 'invoice-section-title', text: 'Плащане' }), field('Начин на плащане', f.payment_method)),
      el('div', { class: 'invoice-summary' }, el('h4', { class: 'invoice-section-title', text: 'Обобщение' }), totalsBox))
  );

  // (Г.3) Класиката беше: започваш фактура → клиентът го няма → затваряш
  // (губиш всичко) → Контакти → отначало. Мини-формата записва клиента и
  // го ИЗБИРА, а фактурата стои непокътната под нея.
  function quickNewClient(prefill) {
    const nf = {
      name: input({ value: prefill || '', placeholder: 'Фирма ООД или име' }),
      eik: input({ value: '', placeholder: '123456789' }),
      vat: input({ value: '', placeholder: 'BG123456789' }),
      mol: input({ value: '', placeholder: 'Име и фамилия' }),
      city: input({ value: '', placeholder: 'Град' }),
      address: input({ value: '', placeholder: 'Улица и номер' }),
      email: input({ type: 'email', value: '', placeholder: 'office@example.com' }),
    };
    const saveClient = async () => {
      const name = nf.name.value.trim();
      if (!name) { toast('Въведете име на клиента.', true); nf.name.focus(); return; }
      try {
        const c = await api('/contacts', { method: 'POST', body: JSON.stringify({
          company_id: state().activeCompany, name, type: 'client',
          eik: nf.eik.value.trim(), vat_number: nf.vat.value.trim(),
          mol: nf.mol.value.trim(), city: nf.city.value.trim(), address: nf.address.value.trim(), email: nf.email.value.trim(),
        }) });
        contacts.push(c);
        f.contact_id.appendChild(el('option', { value: c.id, text: c.name }));
        f.contact_id.value = String(c.id);
        clientBox.setValue(c.id);
        f.contact_id.dispatchEvent(new Event('change', { bubbles: true }));
        closeModal();
        toast('Клиентът „' + c.name + '" е създаден и избран.');
      } catch (e) { toast(e.message, true); }
    };
    openModal({
      title: 'Нов клиент',
      body: el('div', {},
        el('p', { style: 'margin:0 0 10px;color:var(--muted);font-size:13px', text: 'Реквизитите се записват в картона на клиента и се използват във фактурата.' }),
        field('Име / Фирма', nf.name),
        row(field('ЕИК / Булстат', nf.eik), field('ДДС номер', nf.vat)),
        row(field('Град', nf.city), field('Адрес', nf.address)),
        // Надписите — като в картона на контакта: по тях „Извлечи от
        // регистъра" попълва полетата (иначе МОЛ-ът оставаше празен).
        row(field('МОЛ (представляващ)', nf.mol), field('Имейл', nf.email))),
      foot: el('div', {},
        el('button', { class: 'btn ghost', text: 'Отказ', onClick: closeModal }),
        el('button', { class: 'btn', text: 'Запази', style: 'margin-left:8px', onClick: saveClient })),
    });
  }

  // Проверката на редовете преди запис. Връща съобщение (и огражда реда)
  // или null. Сървърът изхвърля напълно празните редове, затова виновният
  // случай е ред с цена/количество, но БЕЗ описание — той влиза в сумата
  // като безименен. Ограждането пада само при поправка (виж drawItems).
  function validateItems() {
    const inputs = [...itemsBody.querySelectorAll('input')].filter((i) => (i.getAttribute('aria-label') || '').startsWith('Описание'));
    inputs.forEach((i) => i.classList.remove('bad'));
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      const hasMoney = (Number(it.unit_price) || 0) !== 0 || (Number(it.quantity) || 0) !== 0;
      if (!String(it.description || '').trim() && hasMoney) {
        if (inputs[i]) { inputs[i].classList.add('bad'); inputs[i].focus(); }
        return 'ред ' + (i + 1) + ' е без описание — попълнете какво се фактурира.';
      }
    }
    // Ред с артикул от каталога (стока) носи мярка (Промени 16.10, т. 3й) —
    // идва сама от артикула; спираме само ако е изтрита на ръка.
    const unitInputs = [...itemsBody.querySelectorAll('input')].filter((i) => (i.getAttribute('aria-label') || '').startsWith('Мярка'));
    unitInputs.forEach((i) => i.classList.remove('bad'));
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.article_id != null && it.article_id !== '' && !String(it.unit || '').trim()) {
        if (unitInputs[i]) { unitInputs[i].classList.add('bad'); unitInputs[i].focus(); }
        return 'ред ' + (i + 1) + ' е артикул от каталога без мярка — попълнете мярката (напр. бр., кг).';
      }
    }
    const filled = items.filter((it) => String(it.description || '').trim() || (Number(it.unit_price) || 0) !== 0);
    if (!filled.length) return (PF ? 'Проформата' : 'Фактурата') + ' е без редове — добавете поне един ред.';
    let sub = 0; items.forEach((it) => sub += (Number(it.quantity) || 0) * (Number(it.unit_price) || 0));
    // 0 € е редно само за окончателна фактура, изцяло покрита от аванс.
    if (Math.round(sub * 100) === 0 && !items.some((it) => it.advance_of != null)) return 'Общата сума е нула — ' + NOUN + 'та трябва да е с положителна стойност. Проверете цените и количествата.';
    return null;
  }

  const save = async (close) => {
    // Коланът под disabled бутона: и програмно повикване не записва.
    if (locked) { toast(PF ? 'Проформата е превърната във фактура и е заключена.' : 'Фактурата е заключена при експорт към Конто — само администратор може да я отключи.', true); return null; }
    // Неразбрана дата не бива да тръгне празна: сървърът слага днешна (ПРОМТ-19).
    if (!dateInputsValid(body)) { toast('Не разбрах една от датите — поправете червеното поле.', true); return null; }
    const p = {
      company_id: state().activeCompany,
      book_start: bookSel ? bookSel.value : (_books[0] ? _books[0].start : undefined),
      contact_id: f.contact_id.value, issue_date: f.issue_date.dataset.iso, due_date: f.due_date.dataset.iso,
      currency: f.currency.value, vat_rate: f.vat_rate.value, payment_method: f.payment_method.value,
      tax_treatment: f.tax_treatment.value, vat_basis: basisValue(),
      notes: f.notes.value, items,
      recurring: f.recurring.checked, recurring_interval: f.recurring_interval.value, recurring_next: f.recurring_next.dataset.iso,
      status: inv.status || 'draft',
      created_by_name: composerSel.value,
      received_by: receivedInput.value,
      doc_type: docTypeSel.value,
      debit_moves_stock: docTypeSel.value === 'debit' && debitMoves.checked,
      advance: docTypeSel.value === 'invoice' && advanceCb.checked,
      ref_invoice_id: docTypeSel.value === 'invoice' ? null : (refSel.value || null),
      ref_invoice_number: docTypeSel.value === 'invoice' ? '' : (((invoicesCache() || []).find((z) => String(z.id) === String(refSel.value)) || {}).number || ''),
    };
    if (PF) {
      // Проформата няма фактурник, тип документ, аванс, повтаряне,
      // съставител/получил — сървърът ги и не приема (routes/proformas.js).
      ['book_start', 'recurring', 'recurring_interval', 'recurring_next', 'created_by_name', 'received_by', 'doc_type', 'debit_moves_stock', 'advance', 'ref_invoice_id', 'ref_invoice_number'].forEach((k) => { delete p[k]; });
    }
    if (!p.contact_id) { toast('Изберете клиент.', true); return null; }
    // (Б.5) Грешките се ловят ТУК, с посочен ред — сървърът отказваше чак
    // след натискане и без да казва кой ред е виновен.
    const bad = validateItems();
    if (bad) { toast(bad, true); return null; }
    if (!PF && basisMissing()) { syncBasis(); (f.vat_basis.value === BASIS_OTHER ? f.vat_basis_other : f.vat_basis).focus(); toast('Фактура с ДДС 0 % трябва да посочва основание за неначисляване на ДДС — изберете го от менюто.', true); return null; }
    try {
      let saved;
      if (existing && existing.id) saved = await api(BASE + '/' + existing.id, { method: 'PUT', body: JSON.stringify(p) });
      else saved = await api(BASE, { method: 'POST', body: JSON.stringify(p) });
      toast('Записано — ' + NOUN + ' № ' + saved.number);
      // warn: фактурата е записана, но складът е на минус — казваме го ясно.
      if (Array.isArray(saved.warnings) && saved.warnings.length) toast('Внимание, склад: ' + saved.warnings.join(' '), true);
      if (close) closeModal(); window.CRM.renderView($('#page')); return saved;
    } catch (e) { toast(e.message, true); return null; }
  };

  const foot = el('div', { style: 'flex-wrap:wrap' });
  if (existing && existing.id) {
    // „Копирай" (в левия ъгъл — решение на Макс): отваря НОВА фактура с
    // всичко от тази (клиент, редове, валута, ДДС, начин на плащане) — без
    // id/номер/дати: датата става днешна, номерът е следващият, статусът
    // „Чернова". Нищо не се записва до „Запази" — моделът на дивидентния мост.
    if (canEdit) foot.appendChild(el('button', { class: 'btn sm ghost copy-btn', text: 'Копирай', title: 'Нова ' + NOUN + ' със същия клиент и редове', style: 'margin-right:auto', onClick: () => {
      closeModal();
      if (PF) {
        invoiceForm({ contact_id: existing.contact_id, currency: existing.currency, vat_rate: existing.vat_rate, payment_method: existing.payment_method, tax_treatment: existing.tax_treatment, vat_basis: existing.vat_basis, notes: existing.notes, due_date: existing.due_date, items: (existing.items || []).map(copyItem) }, contacts, o);
        return;
      }
      invoiceForm({
        contact_id: existing.contact_id,
        currency: existing.currency,
        vat_rate: existing.vat_rate,
        payment_method: existing.payment_method,
        tax_treatment: existing.tax_treatment,
        vat_basis: existing.vat_basis,
        notes: existing.notes,
        doc_type: existing.doc_type,
        debit_moves_stock: existing.debit_moves_stock,
        // Копие на авансова е пак авансова (втори аванс към същия клиент);
        // отметката се вижда горе и се маха с едно щракване.
        advance: existing.advance,
        ref_invoice_id: existing.ref_invoice_id,
        items: (existing.items || []).map(copyItem),
      }, contacts);
    } }));
    if (PF) foot.appendChild(el('button', { class: 'btn sm ghost', text: 'Печат / PDF', onClick: () => window.open('/api/proformas/' + existing.id + '/pdf', '_blank') }));
    else {
      foot.appendChild(el('button', { class: 'btn sm ghost', text: 'Оригинал (PDF)', onClick: () => window.open('/api/invoices/' + existing.id + '/pdf?copy=original', '_blank') }));
      foot.appendChild(el('button', { class: 'btn sm ghost', text: 'Копие (PDF)', onClick: () => window.open('/api/invoices/' + existing.id + '/pdf?copy=copy', '_blank') }));
    }
    // Сървърът можеше да праща фактура по имейл, но екранът нямаше бутон —
    // имаха го само проформите (Промени 16.10, т. 3б). Изпращането не е
    // редакция: позволено е и на заключена фактура.
    // Без право за промяна сървърът отказва изпращането — бутонът го няма.
    if (canEdit) foot.appendChild(el('button', { class: 'btn sm ghost', text: 'Изпрати по имейл', onClick: () => mailDocOverlay(PF ? 'proformas' : 'invoices', existing, contacts, {
      // След изпращане: статусът във формата и в списъка отзад става
      // „Изпратена" веднага (Макс, 02.11.2026 — оставаше „Чернова").
      onSent: () => { statusSel.value = existing.status || 'draft'; syncPaidBtn(); window.CRM.renderView($('#page')); },
    }) }));
    // (Б.2) Статусът вече не е гол select, който записва мигновено:
    // етикет „Статус:" + потвърждение (отказът връща старата стойност).
    // Най-честият случай — плащане — е голям изричен бутон без питане:
    // изричното име Е съгласието. Статусът работи И на заключена фактура
    // (Макс, 01.09.2026): плащането идва след Конто експорта, а отметката
    // му не пипа счетоводното съдържание — сървърът пази същото правило.
    const patchStatus = async (next) => {
      await api(BASE + '/' + existing.id + '/status', { method: 'PATCH', body: JSON.stringify({ status: next }) });
      existing.status = next;
      window.CRM.renderView($('#page'));
    };
    const statusSel = el('select', { id: 'inv-status-' + existing.id, onChange: async (e) => {
      const next = e.target.value;
      if (next === existing.status) return;
      const label = STATUSES[next] ? STATUSES[next][0] : next;
      if (!(await confirmDialog('Да сменя ли статуса на ' + NOUN + ' № ' + existing.number + ' на „' + label + '"?', { okText: 'Промени' }))) {
        e.target.value = existing.status;
        return;
      }
      try { await patchStatus(next); statusSel.value = next; syncPaidBtn(); toast('Статусът е сменен на „' + label + '".'); }
      catch (err) { e.target.value = existing.status; toast(err.message, true); }
    } },
      // Проформата: „Фактурирана" се слага само от „Превърни във фактура".
      ...Object.keys(STATUSES).filter((s) => !PF || s !== 'invoiced' || existing.status === 'invoiced').map((s) => el('option', { value: s, text: STATUSES[s][0], selected: (existing.status || 'draft') === s })));
    statusSel.style.padding = '6px 10px'; statusSel.style.borderRadius = '8px'; statusSel.style.border = '1px solid var(--border)';
    const paidBtn = el('button', { class: 'btn sm ok', text: 'Отбележи като платена', onClick: async () => {
      try {
        await patchStatus('paid');
        statusSel.value = 'paid';
        syncPaidBtn();
        toast('Фактура № ' + existing.number + ' е отбелязана като платена.');
      } catch (err) { toast(err.message, true); }
    } });
    function syncPaidBtn() { paidBtn.style.display = (PF || existing.status === 'paid') ? 'none' : ''; }
    syncPaidBtn();
    if (!PF) foot.appendChild(paidBtn);
    foot.appendChild(el('label', { for: statusSel.id, text: 'Статус:', style: 'align-self:center;color:var(--muted);font-size:13px' }));
    foot.appendChild(statusSel);
    if (PF && typeof o.extraFoot === 'function') { const extra = o.extraFoot(existing, contacts); if (extra) foot.appendChild(extra); }
    if (isAdmin) foot.appendChild(el('button', { class: 'btn sm danger', text: 'Изтрий', onClick: async () => { if (await confirmDialog('Да изтрия ли тази ' + NOUN + '?', { okText: 'Изтрий', danger: true })) { try { await api(BASE + '/' + existing.id, { method: 'DELETE' }); closeModal(); toast('Изтрито.'); window.CRM.renderView($('#page')); } catch (e) { toast('Изтриването не се получи. ' + e.message, true); } } } }));
  }
  // „Отключи" вижда САМО админ и само на заключена фактура: иска причина
  // (задължителна, влиза в историята) през модален диалог.
  if (locked && isAdmin && !PF) {
    foot.appendChild(el('button', { class: 'btn sm', text: 'Отключи', onClick: () => unlockDialog(existing) }));
  }
  foot.appendChild(el('button', { class: 'btn ghost', text: 'Отказ', onClick: closeModal }));
  foot.appendChild(el('button', { class: 'btn', text: 'Запази', onClick: () => save(true) }));
  if (existing && existing.id) {
    // Историята показва промените ПО ПОЛЕТА; номерата на клиенти и машинните
    // статуси се превеждат до имена, за да е четимо за счетоводителя.
    const _ab = auditBlock(existing, { resolve: (fieldName, v) => {
      if (v == null || v === '') return null;
      if (fieldName === 'contact_id') { const c = (contacts || []).find((x) => String(x.id) === String(v)); return c ? c.name : null; }
      if (fieldName === 'status') return (STATUSES[v] || [null])[0];
      if (fieldName === 'doc_type') return ({ invoice: 'Фактура', debit: 'Дебитно известие', credit: 'Кредитно известие' })[v] || null;
      if (fieldName === 'ref_invoice_id') { const r = (invoicesCache() || []).find((x) => String(x.id) === String(v)); return r ? '№ ' + r.number : null; }
      return null;
    } });
    if (_ab) body.appendChild(_ab);
  }
  // Заключването изключва всичко в тялото; в долния ред остават четящите
  // изходи (PDF, „Копирай", „Отказ", „Отключи" за админ) И статус
  // контролите — селектът и „Отбележи като платена" (Макс, 01.09.2026):
  // статусът е оперативна отметка, не счетоводно съдържание.
  if (locked) {
    [...body.querySelectorAll('input, select, textarea, button')].forEach((n) => { n.disabled = true; });
    const KEEP = ['Копирай', 'Оригинал (PDF)', 'Копие (PDF)', 'Печат / PDF', 'Изпрати по имейл', 'Отказ', 'Отключи', 'Отбележи като платена'];
    [...foot.querySelectorAll('button, select')].forEach((n) => {
      // Единственият селект в долния ред е статусният — той остава жив при
      // фактурата; превърнатата проформа е изцяло само за четене.
      if ((n.tagName === 'SELECT' && PF) || (n.tagName !== 'SELECT' && !KEEP.includes(n.textContent))) n.disabled = true;
    });
  }
  const overlay = openModal({ title: existing && existing.id ? (PF ? 'Проформа № ' : 'Фактура № ') + existing.number : (PF ? 'Нова проформа' : 'Нова фактура'), body, foot, wide: true });
  overlay.classList.add('invoice-overlay');
}

// Диалогът за отключване: причината е задължителна и влиза в историята.
// Минава през openModal (фокусът остава вътре) и field() (етикет ↔ поле).
function unlockDialog(inv) {
  const reason = el('textarea', { placeholder: 'Например: сгрешена дата на издаване', maxlength: '500' });
  const doUnlock = async () => {
    if (!reason.value.trim()) { toast('Въведете причина — тя се записва в историята на документа.', true); reason.focus(); return; }
    try {
      await api('/invoices/' + inv.id + '/unlock', { method: 'POST', body: JSON.stringify({ reason: reason.value.trim() }) });
      closeModal(); // диалогът с причината
      closeModal(); // и формата — тя е строена като заключена, ще се отвори отключена
      toast('Фактура № ' + inv.number + ' е отключена.');
      window.CRM.renderView($('#page'));
    } catch (e) { toast(e.message, true); }
  };
  openModal({
    title: 'Отключване на фактура № ' + inv.number,
    body: el('div', {},
      el('p', { style: 'margin:0 0 10px', text: 'Фактурата е заключена при експорт към Конто. Причината за отключването е задължителна и остава в историята на документа.' }),
      field('Причина', reason)),
    foot: el('div', {},
      el('button', { class: 'btn ghost', text: 'Отказ', onClick: closeModal }),
      el('button', { class: 'btn', text: 'Отключи', onClick: doUnlock, style: 'margin-left:8px' })),
  });
}

window.invoiceForm = invoiceForm;
